import { ApplicationConfig, ErrorHandler, LOCALE_ID, inject, isDevMode, provideAppInitializer, provideZonelessChangeDetection } from "@angular/core";
import { registerLocaleData } from "@angular/common";
import localeEnGb from "@angular/common/locales/en-GB";
import localeEs from "@angular/common/locales/es";
import localeHe from "@angular/common/locales/he";
import localeFr from "@angular/common/locales/fr";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from "@angular/common/http";
import { TitleStrategy, provideRouter, withInMemoryScrolling } from "@angular/router";
import { AuthHttpInterceptor, provideAuth0 } from "@auth0/auth0-angular";
import { provideTransloco, TranslocoService } from "@jsverse/transloco";
import { provideStore } from "@ngxs/store";
import { withNgxsReduxDevtoolsPlugin } from "@ngxs/devtools-plugin";
import * as Sentry from "@sentry/angular";
import { withNgxsResetPlugin } from "ngxs-reset-plugin";
import { provideGlobalSortableOptions } from "nxt-sortablejs";
import { firstValueFrom } from "rxjs";
import { AppContextInterceptor, AuthErrorInterceptor, provideAnalytics } from "@uplift-sdk";
import { ENVIRONMENT_TOKEN } from "@sdk/lib/types/environment.token";
import { AuthConfigToken, ComponentMapToken } from "@sdk/lib/values/tokens";
import { getAngularLocaleId } from "@sdk/lib/values/supported-languages";
import { provideToastr } from "@sdk/lib/toastr/toastr.config";
import { provideUserIdleConfig } from "@sdk/lib/user-idle/user-idle.service";
import { environment } from "../environments/environment";
import { APP_ROUTES } from "./app.routes";
import { componentMapConfig } from "./block-component-map";
import { LocalizationService } from "./services/localization.service";
import { TranslocoHttpLoader } from "./services/transloco-http-loader";
import { TranslocoTitleStrategy } from "./services/transloco-title.strategy";
import { GroupState } from "./states/group-state";
import { JournalItemEntryState } from "./states/journal-item-entry.state";
import { MyNotificationsListState } from "./states/my-notifications-list.state";
import { OrganisationState } from "./states/organisation.state";
import { PatientDashboardState } from "./states/patient-dashboard.state";
import { ProfileState } from "./states/profile.state";
import { ProtocolState } from "./states/protocol.state";
import { QuestState } from "./states/quest.state";
import { UpliftUserModuleConfig } from "./types/module-config";
import { withStaleChunkReload } from "@sdk/lib/utils/stale-chunk-reload";

registerLocaleData(localeEnGb);
registerLocaleData(localeEs);
registerLocaleData(localeHe);
registerLocaleData(localeFr);

const authModuleConfig: UpliftUserModuleConfig = {
  stitch: environment,
};

const redirectUri = (): string => {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname.endsWith(".web.app") || hostname.endsWith(".mindlife.app") || hostname.endsWith(".upliftx.net")) {
    return `${window.location.origin}/callback`;
  }
  return environment.auth0.redirectUri;
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(
      APP_ROUTES,
      withInMemoryScrolling({ anchorScrolling: "enabled", scrollPositionRestoration: "enabled" }),
      withStaleChunkReload(),
    ),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AppContextInterceptor, multi: true },
    provideAuth0({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      cacheLocation: "localstorage",
      useRefreshTokens: environment.auth0.useRefreshTokens,
      authorizationParams: {
        redirect_uri: redirectUri(),
        audience: environment.auth0.audience,
        prompt: "login",
      },
      httpInterceptor: {
        allowedList: [
          { uri: `${environment.apiUrl}/api/auth/*`, allowAnonymous: true },
          { uri: `${environment.apiUrl}/api/enrollment/*`, allowAnonymous: true },
          {
            uri: `${environment.apiUrl}/*`,
            tokenOptions: {
              authorizationParams: {
                audience: environment.auth0.audience,
              },
            },
          },
        ],
      },
    }),
    provideAnalytics({
      app: "uplift-user",
      posthogKey: environment.posthog?.key ?? "",
      posthogHost: environment.posthog?.host,
      environment: environment.production ? "production" : "staging",
      strict: !environment.production,
      apiUrl: environment.apiUrl,
    }),
    provideStore(
      [
        GroupState,
        ProtocolState,
        ProfileState,
        QuestState,
        JournalItemEntryState,
        MyNotificationsListState,
        PatientDashboardState,
        OrganisationState,
      ],
      withNgxsReduxDevtoolsPlugin(),
      withNgxsResetPlugin(),
    ),
    provideGlobalSortableOptions({ animation: 150 }),
    provideToastr({
      preventDuplicates: true,
      positionClass: "toast-top-right",
      toastClass: "tw-toast",
      iconClasses: {
        error: "tw-toast-error",
        info: "tw-toast-info",
        success: "tw-toast-success",
        warning: "tw-toast-warning",
      },
    }),
    provideUserIdleConfig({ idle: 1200, timeout: 600, ping: 60 }),
    { provide: ComponentMapToken, useValue: componentMapConfig },
    { provide: AuthConfigToken, useValue: authModuleConfig },
    { provide: ENVIRONMENT_TOKEN, useValue: environment },
    provideTransloco({
      config: {
        availableLangs: ["en", "es", "he", "fr"],
        defaultLang: "en",
        fallbackLang: "en",
        reRenderOnLangChange: true,
        missingHandler: { useFallbackTranslation: true },
        prodMode: !isDevMode(),
        failedRetries: 2,
      },
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(() => {
      const transloco = inject(TranslocoService);
      const lang = localStorage.getItem(LocalizationService.LANG_KEY) || transloco.getDefaultLang();
      transloco.setActiveLang(lang);
      return firstValueFrom(transloco.load(lang));
    }),
    { provide: TitleStrategy, useClass: TranslocoTitleStrategy },
    {
      provide: LOCALE_ID,
      useFactory: () => getAngularLocaleId(localStorage.getItem(LocalizationService.LANG_KEY)),
    },
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
  ],
};
